import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Package, Folder, Sparkles, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { apiRequest, formatPrice } from '../lib/utils';
import {
    Dialog,
    DialogContent,
} from './ui/dialog';

export default function GlobalSearch() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Keyboard shortcut (Ctrl+K or Cmd+K)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
                e.preventDefault();
                setOpen(true);
            }
            if (e.key === 'Escape') {
                setOpen(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    // Focus input when dialog opens
    useEffect(() => {
        if (open && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    }, [open]);

    const handleSearch = async (searchQuery) => {
        setQuery(searchQuery);
        
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchQuery.length < 2) {
            setResults([]);
            return;
        }

        searchTimeoutRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const data = await apiRequest(`/search?q=${encodeURIComponent(searchQuery)}`);
                setResults(data.results || []);
            } catch (error) {
                console.error('Search error:', error);
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 300);
    };

    const handleSelect = (result) => {
        setOpen(false);
        setQuery('');
        setResults([]);
        navigate(result.link);
    };

    const getIcon = (type) => {
        switch (type) {
            case 'product':
                return <Package className="h-4 w-4 text-primary" />;
            case 'category':
                return <Folder className="h-4 w-4 text-amber-500" />;
            case 'feature':
                return <Sparkles className="h-4 w-4 text-purple-500" />;
            default:
                return <Search className="h-4 w-4" />;
        }
    };

    return (
        <>
            {/* Search trigger button */}
            <Button
                variant="outline"
                className="relative h-9 w-9 p-0 xl:h-10 xl:w-60 xl:justify-start xl:px-3 xl:py-2"
                onClick={() => setOpen(true)}
                data-testid="global-search-btn"
            >
                <Search className="h-4 w-4 xl:mr-2" />
                <span className="hidden xl:inline-flex">Rechercher...</span>
                <kbd className="pointer-events-none absolute right-1.5 top-1.5 hidden h-6 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 xl:flex">
                    <span className="text-xs">⌘</span>K
                </kbd>
            </Button>

            {/* Search dialog */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="overflow-hidden p-0 shadow-lg max-w-lg">
                    <div className="flex items-center border-b px-3">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <Input
                            ref={inputRef}
                            placeholder="Rechercher produits, catégories, fonctionnalités..."
                            value={query}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none border-0 focus-visible:ring-0 placeholder:text-muted-foreground"
                            data-testid="global-search-input"
                        />
                        {query && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6"
                                onClick={() => {
                                    setQuery('');
                                    setResults([]);
                                }}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        )}
                    </div>

                    <div className="max-h-[300px] overflow-y-auto p-2">
                        {loading && (
                            <div className="flex items-center justify-center py-6">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            </div>
                        )}

                        {!loading && results.length === 0 && query.length >= 2 && (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                Aucun résultat pour "{query}"
                            </div>
                        )}

                        {!loading && results.length === 0 && query.length < 2 && (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                Tapez au moins 2 caractères pour rechercher
                            </div>
                        )}

                        {!loading && results.length > 0 && (
                            <div className="space-y-1">
                                {results.map((result, index) => (
                                    <button
                                        key={`${result.type}-${result.id}`}
                                        onClick={() => handleSelect(result)}
                                        className="w-full flex items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted transition-colors"
                                        data-testid={`search-result-${index}`}
                                    >
                                        {result.image ? (
                                            <img 
                                                src={result.image} 
                                                alt="" 
                                                className="h-10 w-10 rounded-md object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
                                                {getIcon(result.type)}
                                            </div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">{result.title}</p>
                                            <p className="text-xs text-muted-foreground truncate">
                                                {result.subtitle}
                                            </p>
                                        </div>
                                        {result.price && (
                                            <span className="text-sm font-semibold text-primary">
                                                {formatPrice(result.price)}
                                            </span>
                                        )}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
